import React, {
  MouseEventHandler,
  forwardRef,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { useIntl } from 'react-intl';

import {
  ReservationFlowSelectedPaymentMethodNewCreditCard,
  ReservationFlowMe,
  ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo,
} from 'src/interfaces/reservation_flow';
import { RSBorderedList } from 'src/reservation-page/bordered-list/RSBorderedList';
import { RSLinkButton } from 'src/reservation-page/RSLinkButton';

import { RegisteredCreditCardItem } from './components/RegisteredCreditCardItem';
import { NewCreditCardItem } from './components/NewCreditCardItem';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { TokenResponse } from '@mul-pay/mptoken-js';

export interface PaymentMethodInputProps {
  value?:
    | ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo
    | ReservationFlowSelectedPaymentMethodNewCreditCard;
  onChange: (
    paymentMethod:
      | ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo
      | ReservationFlowSelectedPaymentMethodNewCreditCard,
  ) => void;
  onBlur: () => void;
  userCreditCard: ReservationFlowMe['credit_card'];
  isMonthlyBilling: boolean;
  handlePaymentElementChange: (event: StripePaymentElementChangeEvent) => void;
  isMulpayOrderEnabled?: boolean;
  handleMulpayTokenChange: (tokenResponse?: TokenResponse) => void;
  handleRegisteredCardSelected?: () => void;
  merchantPublicId: string;
}

export const PaymentMethodInput = forwardRef<
  HTMLDivElement,
  PaymentMethodInputProps
>(
  (
    {
      value,
      onChange,
      onBlur,
      userCreditCard,
      isMonthlyBilling,
      handlePaymentElementChange,
      isMulpayOrderEnabled,
      handleMulpayTokenChange,
      handleRegisteredCardSelected,
      merchantPublicId,
    },
    ref,
  ) => {
    const intl = useIntl();

    const [isOtherPaymentMethodCollapsed, setIsOtherPaymentMethodCollapsed] =
      useState(typeof value === 'undefined' || value.type !== 'credit_card');

    const onRequireOtherPaymentMethodPresentingButtonClick = useCallback<
      MouseEventHandler<HTMLButtonElement>
    >((event) => {
      event.stopPropagation();
      setIsOtherPaymentMethodCollapsed(false);
    }, []);

    useEffect(() => {
      if (typeof value === 'undefined') return;
      if (value.type === 'credit_card') return;

      setIsOtherPaymentMethodCollapsed(false);
    }, [value]);

    return (
      <div ref={ref} className="space-y-2.5">
        <div>
          <RSBorderedList>
            <RegisteredCreditCardItem
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              userCreditCard={userCreditCard}
              hundleSelected={handleRegisteredCardSelected}
            />
          </RSBorderedList>
        </div>

        {(() => {
          if (isOtherPaymentMethodCollapsed) {
            return (
              <div className="flex justify-center">
                <div className="rs-font text-16 pc:text-15 font-normal">
                  <RSLinkButton
                    type="button"
                    onClick={onRequireOtherPaymentMethodPresentingButtonClick}
                  >
                    {intl.formatMessage({
                      defaultMessage: '他のお支払い方法にする',
                      id: 'I0ZwCE',
                      description:
                        'src/organisms/product-purchase/PaymentMethodInput/index.tsx',
                    })}
                  </RSLinkButton>
                </div>
              </div>
            );
          }

          return (
            <div>
              <RSBorderedList>
                <NewCreditCardItem
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  isMonthlyBilling={isMonthlyBilling}
                  handlePaymentElementChange={handlePaymentElementChange}
                  isMulpayOrderEnabled={isMulpayOrderEnabled}
                  handleMulpayTokenChange={handleMulpayTokenChange}
                  merchantPublicId={merchantPublicId}
                />
              </RSBorderedList>
            </div>
          );
        })()}
      </div>
    );
  },
);
